import React, { useEffect, useMemo, useState } from "react";
import {
  FiMail,
  FiSearch,
  FiEye,
  FiCheckCircle,
  FiRefreshCw,
  FiTrash2,
  FiX,
} from "react-icons/fi";

import { db } from "../lib/firebase";
import {
  collection,
  query as fsQuery,
  where,
  orderBy,
  limit as fsLimit,
  getDocs,
  getCountFromServer,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

const normalizeStatus = (s) => String(s || "").toUpperCase();

const getId = (m) => m?.id || m?._id || m?.messageId || m?.ticketId;

const safeText = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" && v.trim() === "") return "—";
  return String(v);
};

const getUserDisplay = (m) => {
  const u = m?.user || m?.userId || m?.fromUser || m?.sender;
  if (!u) return "—";
  if (typeof u === "string") return u;
  return (
    u?.fullName ||
    u?.name ||
    u?.displayName ||
    u?.email ||
    u?._id ||
    u?.id ||
    "—"
  );
};

const badgeClass = (s) => {
  const st = normalizeStatus(s);
  if (st === "RESOLVED") return "bg-green-100 text-green-700";
  if (st === "OPEN") return "bg-orange-100 text-orange-700";
  return "bg-gray-100 text-gray-700";
};

export const filterMessages = (messages, filter, q) => {
  const query = String(q || "").trim().toLowerCase();
  return (messages || []).filter((m) => {
    const st = normalizeStatus(m.status || m.state || "OPEN");
    const passesStatus = filter === "ALL" ? true : st === filter;
    if (!passesStatus) return false;
    if (!query) return true;
    const hay = [
      getUserDisplay(m),
      m.name,
      m.fullName,
      m.email,
      m.phone,
      m.mobile,
      m.title,
      m.subject,
      m.message,
      m.body,
      m.description,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(query);
  });
};

 

const ContactDetailsModal = ({ message, onClose, onResolve, onReopen, onDelete }) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const id = getId(message);
    if (!db || !id) return;
    const threadRef = doc(db, "admin_support_chats", String(id));
    const msgsRef = collection(threadRef, "messages");
    const q = fsQuery(msgsRef, orderBy("timestamp", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => {
        const data = d.data();
        const ts = data.timestamp || data.createdAt || data.updatedAt;
        return {
          id: d.id,
          text: data.text || data.message || "",
          sender: data.sender || data.senderId || data.from || "user",
          createdAt: ts && ts.toDate ? ts.toDate() : ts || null,
        };
      });
      setChatMessages(items);
    });
    return () => unsub();
  }, [message]);

  const sendReply = async () => {
    const id = getId(message);
    if (!db || !id) return;
    const text = replyText.trim();
    if (!text) return;
    try {
      setSending(true);
      const threadRef = doc(db, "admin_support_chats", String(id));
      const msgsRef = collection(threadRef, "messages");
      await addDoc(msgsRef, {
        text,
        sender: "admin",
        senderId: "admin",
        timestamp: serverTimestamp(),
      });
      await updateDoc(threadRef, {
        lastMessage: { text, senderId: "admin", timestamp: serverTimestamp() },
        lastSender: "admin",
        updatedAt: serverTimestamp(),
      });
      setReplyText("");
    } finally {
      setSending(false);
    }
  };

  if (!message) return null;
  const status = normalizeStatus(message.status || message.state || message.ticketStatus || "OPEN");
  const createdAt = message.createdAt || message.created || message.created_on || message.timestamp;
  const title = message.title || message.subject || "Contact Message";

  return (
    <div className="fixed inset-0 bg-[var(--black)] flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border-2 border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-[var(--primary-solid)]">{title}</h3>
            <div className="mt-1 text-sm text-[var(--black-text)]/70">
              {createdAt ? new Date(createdAt).toLocaleString() : "—"}
              <span className="mx-2">•</span>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badgeClass(status)}`}>
                {status || "—"}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
            title="Close"
          >
            <FiX />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">User</div>
              <div className="mt-1 flex items-center gap-3">
                { (message.userImage || (message.user && message.user.image)) ? (
                  <img
                    src={message.userImage || (message.user && message.user.image) || ""}
                    alt={safeText(message.userName || getUserDisplay(message))}
                    className="w-10 h-10 rounded-full object-cover border border-gray-200"
                  />
                ) : null }
                <div className="text-sm text-[var(--black-text)]">{getUserDisplay(message)}</div>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</div>
              <div className="mt-1 text-sm text-[var(--black-text)] space-y-1">
                <div><span className="text-gray-500">Name:</span> {safeText(message.name || message.fullName)}</div>
                <div><span className="text-gray-500">Phone:</span> {safeText(message.phone || message.mobile)}</div>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Chat</div>
            <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
              {chatMessages.length === 0 ? (
                <div className="text-sm text-[var(--black-text)]/70">No messages yet</div>
              ) : (
                chatMessages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                        m.sender === "admin"
                          ? "bg-[var(--primary-solid)] text-white"
                          : "bg-gray-100 text-[var(--black-text)]"
                      }`}
                    >
                      <div>{m.text}</div>
                      <div className="mt-1 text-[10px] opacity-70">
                        {m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={status === "RESOLVED" ? "Thread resolved" : "Type a reply..."}
                disabled={status === "RESOLVED" || sending}
                className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
              />
              <button
                onClick={sendReply}
                disabled={status === "RESOLVED" || sending || !replyText.trim()}
                className="px-4 py-2 bg-[var(--yellow)] text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
              >
                Send
              </button>
            </div>
          </div>

         
        </div>

        <div className="p-6 border-t border-gray-200 flex flex-wrap items-center justify-end gap-3">
          {status === "RESOLVED" ? (
            <button
              onClick={() => onReopen(getId(message))}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg text-[var(--black-text)] hover:bg-gray-50 flex items-center gap-2"
            >
              <FiRefreshCw />
              Reopen
            </button>
          ) : (
            <button
              onClick={() => onResolve(getId(message))}
              className="px-4 py-2 bg-[var(--yellow)] text-white rounded-lg font-semibold hover:opacity-90 flex items-center gap-2"
            >
              <FiCheckCircle />
              Mark Resolved
            </button>
          )}

          <button
            onClick={() => onDelete(getId(message))}
            className="px-4 py-2 border-2 border-red-200 rounded-lg text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <FiTrash2 />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const ContactUs = () => {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("ALL"); // ALL | OPEN | RESOLVED
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0 });
  const [stats, setStats] = useState({ total: 0, open: 0, resolved: 0 });
  const [errorMsg, setErrorMsg] = useState("");

  const filteredLocal = useMemo(() => filterMessages(messages, filter, q), [messages, filter, q]);

  const fetchStats = async () => {
    if (!db) return;
    const base = collection(db, "admin_support_chats");
    const totalSnap = await getCountFromServer(fsQuery(base));
    const openSnap = await getCountFromServer(fsQuery(base, where("status", "==", "OPEN")));
    const resolvedSnap = await getCountFromServer(fsQuery(base, where("status", "==", "RESOLVED")));
    setStats({
      total: totalSnap.data().count || 0,
      open: openSnap.data().count || 0,
      resolved: resolvedSnap.data().count || 0,
    });
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      if (!db) {
        setMessages([]);
        setPagination({ currentPage: 1, totalPages: 1, totalRecords: 0 });
        setErrorMsg("Firebase not configured");
        return;
      }
      const base = collection(db, "admin_support_chats");
      let qRef =
        filter !== "ALL"
          ? fsQuery(
              base,
              where("status", "==", filter),
              orderBy("updatedAt", "desc"),
              fsLimit(50)
            )
          : fsQuery(base, orderBy("updatedAt", "desc"), fsLimit(50));
      const snap = await getDocs(qRef);
      const records = snap.docs.map((d) => {
        const data = d.data();
        const ts =
          (data.updatedAt && data.updatedAt.toDate ? data.updatedAt.toDate() : data.updatedAt) ||
          (data.timestamp && data.timestamp.toDate ? data.timestamp.toDate() : data.timestamp) ||
          null;
        return {
          id: d.id,
          status: normalizeStatus(data.status || "OPEN"),
          createdAt: ts,
          name: data.userName || "",
          email: data.userEmail || "",
          subject: data.subject || data.title || "",
          phone: data.userMobile || "",
          message:
            (data.lastMessage && (data.lastMessage.text || data.lastMessage.message)) ||
            data.text ||
            "",
          user: {
            uid: data.userId,
            fullName: data.userName,
            phone: data.userMobile,
            image: data.userImage,
          },
        };
      });
      setMessages(records);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalRecords: records.length,
      });
      const byStatus = records.reduce(
        (acc, m) => {
          const st = normalizeStatus(m.status || "OPEN");
          acc.total += 1;
          if (st === "RESOLVED") acc.resolved += 1;
          else acc.open += 1;
          return acc;
        },
        { total: 0, open: 0, resolved: 0 }
      );
      setStats(byStatus);
    } catch (err) {
      setMessages([]);
      setPagination({ currentPage: 1, totalPages: 1, totalRecords: 0 });
      setErrorMsg("Failed to load chats from Firebase");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page]);

  const handleSearch = () => {
    setPage(1);
    fetchMessages();
  };

  const updateStatus = async (id, nextStatus) => {
    if (!id || !db) return;
    const ref = doc(db, "admin_support_chats", String(id));
    await updateDoc(ref, {
      status: nextStatus,
      resolvedAt: nextStatus === "RESOLVED" ? serverTimestamp() : null,
      updatedAt: serverTimestamp(),
    });
    if (nextStatus === "RESOLVED") {
      setSelected(null);
    }
    await fetchStats();
    await fetchMessages();
  };

  const handleDelete = async (id) => {
    if (!id || !db) return;
    if (!window.confirm("Delete this chat thread?")) return;
    const ref = doc(db, "admin_support_chats", String(id));
    await deleteDoc(ref);
    setSelected(null);
    await fetchStats();
    await fetchMessages();
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-[var(--primary-solid)] flex items-center gap-3">
            <FiMail className="text-3xl" />
            Contact Us
          </h1>
          <p className="text-[var(--black-text)]/70 mt-2">
            View messages submitted by users from the app
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-2 border-blue-500/20 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-400 uppercase tracking-wider">Total</p>
                <p className="text-3xl font-bold text-blue-400">{stats.total}</p>
              </div>
              <div className="text-4xl text-blue-400">
                <FiMail />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-2 border-orange-500/20 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-400 uppercase tracking-wider">Open</p>
                <p className="text-3xl font-bold text-orange-400">{stats.open}</p>
              </div>
              <div className="text-4xl text-orange-400">
                <FiRefreshCw />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-2 border-green-500/20 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-400 uppercase tracking-wider">Resolved</p>
                <p className="text-3xl font-bold text-green-400">{stats.resolved}</p>
              </div>
              <div className="text-4xl text-green-400">
                <FiCheckCircle />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-[var(--black-text)] mb-2">
                Search
              </label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-3 text-[var(--primary-solid)]" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, phone, user, message..."
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 bg-white text-[var(--black-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
              </div>
            </div>

            <div className="w-full md:w-56">
              <label className="block text-sm font-medium text-[var(--black-text)] mb-2">
                Status
              </label>
              <select
                value={filter}
                onChange={(e) => {
                  setPage(1);
                  setFilter(e.target.value);
                }}
                className="w-full px-4 py-2 border-2 border-gray-200 text-[var(--black-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)]"
              >
                <option value="ALL">All</option>
                <option value="OPEN">Open</option>
                <option value="RESOLVED">Resolved</option>
              </select>
            </div>

            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-[var(--yellow)] text-white rounded-lg font-semibold hover:opacity-90"
            >
              Search
            </button>
          </div>

          {errorMsg ? (
            <div className="mt-4 text-sm text-red-600">{errorMsg}</div>
          ) : null}
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
          <div className="overflow-x-auto overflow-y-auto max-h-[600px] bg-white rounded-2xl shadow-lg border-2 border-gray-200 w-full">
            <table className="w-full text-sm text-[var(--black-text)] min-w-[1000px]">
              <thead>
                <tr className="bg-[var(--yellow)] text-white text-xs uppercase font-semibold">
                 
                  <th className="py-3 px-2 text-left">Name</th>
                  <th className="py-3 px-2 text-left">Phone</th>
                  <th className="py-3 px-2 text-left">Subject</th>
                  <th className="py-3 px-2 text-left">Message</th>
                  <th className="py-3 px-2 text-left">Status</th>
                  <th className="py-3 px-2 text-left">Created</th>
                  <th className="py-3 px-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-10">
                      <div className="flex justify-center items-center">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-[var(--primary-solid)] rounded-full animate-spin" />
                      </div>
                    </td>
                  </tr>
                ) : filteredLocal.length > 0 ? (
                  filteredLocal.map((m) => {
                    const id = getId(m) || "—";
                    const status = normalizeStatus(m.status || m.state || "OPEN") || "OPEN";
                    const createdAt = m.createdAt || m.created || m.timestamp;
                    const preview = safeText(m.message || m.body || m.description || m.text);

                    return (
                      <tr
                        key={String(id) + String(createdAt || "")}
                        className="border-b last:border-0 border-gray-200 hover:bg-purple-400"
                      >
                     
                        <td className="py-3 px-2 text-xs truncate max-w-[160px]">
                          {safeText(m.name || m.fullName)}
                        </td>
                        <td className="py-3 px-2 text-xs whitespace-nowrap">
                          {safeText(m.phone || m.mobile)}
                        </td>
                        <td className="py-3 px-2 text-xs truncate max-w-[200px]">
                          {safeText(m.subject || m.title)}
                        </td>
                        <td className="py-3 px-2 text-xs truncate max-w-[260px] text-[var(--black-text)]/70">
                          {preview}
                        </td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${badgeClass(status)}`}>
                            {status}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-xs whitespace-nowrap text-[var(--black-text)]/70">
                          {createdAt ? new Date(createdAt).toLocaleString() : "—"}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <button
                              onClick={() => setSelected(m)}
                              className="text-xs hover:text-[var(--primary-solid)] flex items-center gap-1"
                              title="View"
                            >
                              <FiEye /> View
                            </button>
                            {status === "RESOLVED" ? (
                              <button
                                onClick={() => updateStatus(getId(m), "OPEN")}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Reopen
                              </button>
                            ) : (
                              <button
                                onClick={() => updateStatus(getId(m), "RESOLVED")}
                                className="text-xs text-green-600 hover:underline"
                              >
                                Resolve
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="py-16">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="w-16 h-16 rounded-md bg-[var(--primary-solid)]/10 flex items-center justify-center border-2 border-gray-200">
                          <FiMail className="text-[var(--primary-solid)] text-2xl" />
                        </div>
                        <div className="text-lg font-semibold text-[var(--primary-solid)]">
                          No contact messages found
                        </div>
                        <div className="text-sm text-[var(--black-text)]/70">
                          Users haven’t submitted any messages yet.
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-[var(--black-text)]/70">
              Showing {filteredLocal.length} of {pagination.totalRecords || stats.total || filteredLocal.length} messages
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if ((pagination.currentPage || page) > 1) setPage((p) => p - 1);
                }}
                className="px-3 py-1 border-2 border-gray-200 rounded disabled:opacity-50 text-[var(--black-text)] hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white"
                disabled={(pagination.currentPage || page) <= 1}
              >
                Prev
              </button>
              <div className="text-sm text-[var(--black-text)]">
                Page {pagination.currentPage || page} / {pagination.totalPages || 1}
              </div>
              <button
                onClick={() => {
                  if ((pagination.currentPage || page) < (pagination.totalPages || 1)) setPage((p) => p + 1);
                }}
                className="px-3 py-1 border-2 border-gray-200 rounded disabled:opacity-50 text-[var(--black-text)] hover:bg-gradient-to-r hover:from-[var(--primary-solid)] hover:to-[var(--primary-dark)] hover:text-white"
                disabled={(pagination.currentPage || page) >= (pagination.totalPages || 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {selected ? (
        <ContactDetailsModal
          message={selected}
          onClose={() => setSelected(null)}
          onResolve={(id) => updateStatus(id, "RESOLVED")}
          onReopen={(id) => updateStatus(id, "OPEN")}
          onDelete={handleDelete}
        />
      ) : null}
    </div>
  );
};

export default ContactUs;
